import {
  AloftDate,
  FilledAddress,
  PriorSalesEntities,
  TKV2_MLS_API,
  TKV2_ORDER_ATTACHMENT_API,
  expected,
  schFilledAddress,
} from '@aloft/toolkit-shared';

import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useParams } from 'react-router';

import {
  fetchGetMLSDataAPI,
  fetchGetTkv2PriorSalesAPI,
  usePutTkv2PriorSalesAPI,
} from '../../hooks/api/toolkitComponents.ts';
import { useToolkitContext } from '../../hooks/api/toolkitContext.ts';
import { setPriorSales } from '../../redux/features/priorSalesSlice.ts';
import { selectEffectiveDate } from '../../redux/features/selectors/orderSelectors.ts';
import { selectPropertyIdentificationEntities } from '../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectTransactionEntities } from '../../redux/features/selectors/propertyDataSelectors/transactionSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';

const SUBJECT_LOOKBACK_CUTOFF_YEARS = 3;
const COMP_LOOKBACK_CUTOFF_YEARS = 1;

const addressesAreEqual = (address: FilledAddress, persistedAddress: FilledAddress) => {
  const { AddressLineText, CityName, PostalCode, StateCode, CountyName, unitNumber } = address;
  const {
    AddressLineText: pAddressLineText,
    CityName: pCityName,
    PostalCode: pPostalCode,
    StateCode: pStateCode,
    CountyName: pCountyName,
    unitNumber: pUnitNumber,
  } = persistedAddress;

  return (
    AddressLineText.toLocaleUpperCase() === pAddressLineText.toLocaleUpperCase() &&
    CityName.toLocaleUpperCase() === pCityName.toLocaleUpperCase() &&
    PostalCode === pPostalCode &&
    StateCode.toLocaleUpperCase() === pStateCode.toLocaleUpperCase() &&
    (!CountyName ||
      !pCountyName ||
      CountyName?.toLocaleUpperCase() === pCountyName?.toLocaleUpperCase()) &&
    (!unitNumber ||
      !pUnitNumber ||
      unitNumber?.toLocaleUpperCase() === pUnitNumber?.toLocaleUpperCase())
  );
};

export const usePriorSalesSync = () => {
  const orderID = expected(useParams().orderId);

  const { mutate: updatePersistedPriorSales } = usePutTkv2PriorSalesAPI();
  const { fetcherOptions } = useToolkitContext();

  const dispatch = useAppDispatch();
  const effectiveDate = useAppSelector(selectEffectiveDate);
  const propertyIdentifications = useAppSelector(selectPropertyIdentificationEntities);
  const transactions = useAppSelector(selectTransactionEntities);

  useEffect(() => {
    if (!effectiveDate) return;

    fetchGetTkv2PriorSalesAPI({ pathParams: { orderID }, ...fetcherOptions })
      .then((persistedPriorSalesData) => {
        const parsedPersistedData =
          TKV2_ORDER_ATTACHMENT_API.PRIOR_SALES.GET.RESPONSE.parse(persistedPriorSalesData).body
            .priorSales;

        const addressMap = Object.fromEntries(
          Object.entries(propertyIdentifications).map(([id, propertyIdentification]) => [
            id,
            schFilledAddress.parse(propertyIdentification.address),
          ]),
        );

        const persistedAddressMap = Object.entries(parsedPersistedData).reduce(
          (acc, [id, priorSalesListings]) => {
            const firstValidRawAddress: Partial<FilledAddress> | undefined = priorSalesListings
              .map<Partial<FilledAddress>>((listing) => ({
                AddressLineText: listing.UnparsedAddress,
                CityName: listing.City,
                PostalCode: listing.PostalCode,
                StateCode: listing.StateOrProvince,
                CountyName: listing.CountyOrParish,
                unitNumber: listing.UnitNumber,
              }))
              .find((rawAddress) => schFilledAddress.safeParse(rawAddress).success);

            if (!firstValidRawAddress) return acc;

            const parsed = schFilledAddress.parse(firstValidRawAddress);

            acc[id] = parsed;
            return acc;
          },
          {} as Record<string, FilledAddress>,
        );

        const addressesToFetchMap = Object.entries(addressMap).reduce(
          (acc, [id, address]) => {
            const persistedAddress = persistedAddressMap[id];
            if (!persistedAddress) {
              acc[id] = address;
              return acc;
            }

            if (!addressesAreEqual(address, persistedAddress)) {
              acc[id] = address;
            }

            return acc;
          },
          {} as Record<string, Partial<FilledAddress>>,
        );

        const fetchPromises = Object.entries(addressesToFetchMap).map(([id, address]) => {
          const effectiveAloftDate = AloftDate.fromIsoString(effectiveDate.isoString);
          const lookbackCutoffYears =
            id === 'subject' ? SUBJECT_LOOKBACK_CUTOFF_YEARS : COMP_LOOKBACK_CUTOFF_YEARS;
          const lookbackCutoff = effectiveAloftDate.subtractMonths(lookbackCutoffYears * 12);

          return fetchGetMLSDataAPI({
            queryParams: {
              ...address,
              lookbackCutoff: lookbackCutoff.toIsoString(),
            },
            ...fetcherOptions,
          });
        });

        Promise.allSettled(fetchPromises)
          .then((results) => {
            const fetchedPriorSalesEntities: PriorSalesEntities = {};

            results.forEach((result, index) => {
              const id = expected(Object.keys(addressesToFetchMap)[index]);

              if (result.status === 'fulfilled') {
                const mlsData = TKV2_MLS_API.GET.RESPONSE.parse(result.value);
                const transaction = transactions[id];

                // Filter out any ongoing listings or any that match the property's listing in the order's analysis section
                const priorListings = mlsData.listings.filter(
                  ({ ListingId }) => ListingId && ListingId !== transaction?.mlsListingID,
                );

                fetchedPriorSalesEntities[id] = priorListings;
              } else {
                console.error(`Failed to fetch MLS data for property ID ${id}:`, result.reason);
              }
            });

            const priorSalesEntities = Object.keys(addressMap).reduce((acc, id) => {
              const fetchedEntity = fetchedPriorSalesEntities[id];
              if (fetchedEntity) {
                acc[id] = fetchedEntity;
              } else {
                acc[id] = parsedPersistedData[id] ?? [];
              }

              return acc;
            }, {} as PriorSalesEntities);

            if (!isEqual(parsedPersistedData, priorSalesEntities)) {
              updatePersistedPriorSales({
                pathParams: { orderID },
                body: {
                  version: 1,
                  priorSales: priorSalesEntities,
                },
              });
            }

            dispatch(setPriorSales(priorSalesEntities));
          })
          .catch((error) => {
            console.error('Error while updating Prior Sales MLS data:', error);
          });
      })
      .catch((error) => {
        console.error('Error fetching persisted prior sales MLS data:', error);
      });
  }, [effectiveDate]);
};
